import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTreeAuth } from '../../lib/treeAuth';
import { LayoutDashboard, Package, Truck, LogOut, User, Menu, X, TreePine, Users, Settings, ShieldCheck, ScanFace } from 'lucide-react';
import { Button } from '../ui/button';
import { TreePasskeyManager } from './TreePasskeyManager';

const WC_LOGO = "https://customer-assets.emergentagent.com/job_tree-logistics/artifacts/7lohxwsd_2025Logo.png";

const navItems = [
  { path: '/trees', label: 'Dashboard', icon: LayoutDashboard, end: true, page: 'dashboard' },
  { path: '/trees/field', label: 'Field', icon: TreePine, page: 'field' },
  { path: '/trees/pallets', label: 'Stock', icon: Package, page: 'stock' },
  { path: '/trees/loads', label: 'Collections', icon: Truck, page: 'collections' },
  { path: '/trees/yard', label: 'Yard Map', icon: Package, page: 'yard' },
  { path: '/trees/users', label: 'Users', icon: Users, adminOnly: true },
  { path: '/trees/roles', label: 'Roles', icon: ShieldCheck, adminOnly: true },
  { path: '/trees/settings', label: 'Settings', icon: Settings, adminOnly: true },
];

export default function TreeLayout({ children }) {
  const { user, logout, permissions, roleName } = useTreeAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasskeys, setShowPasskeys] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/trees/login');
  };

  const closeSidebar = () => setSidebarOpen(false);
  const isAdmin = user?.role === 'admin';
  const visibleItems = navItems.filter((item) => {
    if (item.adminOnly) return isAdmin;
    return isAdmin || permissions?.[item.page]?.access;
  });

  return (
    <div className="min-h-screen bg-[#F4F1ED]" data-testid="tree-app-layout">
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#1A4331]/15 px-4 py-3 flex items-center justify-between no-print">
        <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)} data-testid="tree-menu-button">
          <Menu className="w-5 h-5 text-[#0A1711]" />
        </Button>
        <img src={WC_LOGO} alt="Wilson Contractors" className="h-7 object-contain" />
        <div className="w-9" />
      </header>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={closeSidebar} />
      )}

      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white/95 backdrop-blur-xl border-r border-[#1A4331]/15 flex flex-col no-print z-50 transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="tree-sidebar"
      >
        <div className="p-5 border-b border-[#1A4331]/10 flex items-center justify-between">
          <div className="flex-1" />
          <img src={WC_LOGO} alt="Wilson Contractors" className="h-12 object-contain" />
          <div className="flex-1 flex justify-end">
            <Button variant="ghost" size="sm" onClick={closeSidebar} className="lg:hidden">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={!!item.end}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#1A4331] text-white' : 'text-[#4A5D53] hover:text-[#0A1711] hover:bg-[#1A4331]/5'}`
              }
              data-testid={`tree-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#1A4331]/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#1A4331]/10 flex items-center justify-center">
              <User className="w-4 h-4 text-[#1A4331]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-[#0A1711] truncate">{user?.name || user?.email}</p>
              <p className="text-[10px] text-[#4A5D53] uppercase tracking-wider">{roleName || user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPasskeys(true)}
            className="w-full justify-start text-[#4A5D53] hover:text-[#1A4331] hover:bg-[#1A4331]/5"
            data-testid="tree-manage-passkey-button"
          >
            <ScanFace className="w-4 h-4 mr-2" />
            Face ID / Touch ID
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start text-[#4A5D53] hover:text-red-600 hover:bg-red-50"
            data-testid="tree-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      <TreePasskeyManager open={showPasskeys} onOpenChange={setShowPasskeys} />

      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-[1400px]">
          {children}
        </div>
      </main>
    </div>
  );
}
